// MUST be first: imports are evaluated before any statement here, so an
// init() call would miss the module-scope throws in @consigli/hooks' config,
// ./app's getMsalInstance() and the missing-root check below.
import './monitoring/instance';

import { reduxStore } from '@consigli/hooks';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { initReactI18next } from 'react-i18next';
import { Provider as ReduxProvider } from 'react-redux';
import { ToastContainer } from 'react-toastify';

import '@aecom-ai-engineering/design-styles/fonts.css';
import '@fontsource-variable/inter';

import { App } from './app';
import './index.css';
import caTranslation from './locales/ca/translation.json';
import deTranslation from './locales/de/translation.json';
import enTranslation from './locales/en/translation.json';
import esTranslation from './locales/es/translation.json';
import frTranslation from './locales/fr/translation.json';
import jaTranslation from './locales/ja/translation.json';
import nbTranslation from './locales/nb/translation.json';
import nnTranslation from './locales/nn/translation.json';
import svTranslation from './locales/sv/translation.json';
import viTranslation from './locales/vi/translation.json';
import zhTranslation from './locales/zh/translation.json';
import { installDevRoleOverride } from './util/dev-role-override';
import { syncDocumentLanguage } from './utils/language';

// Before init(), deliberately, so the listener is registered in time for the
// languageChanged that init() emits. That emit does not depend on anything
// exotic: init()'s load() only returns early on `languages && !isInitialized`,
// which is false on a fresh instance (languages unset) and false here too,
// because @consigli/facade already init'd this same default instance at import
// time. Facade's head start is what makes the eager write inside pick up an
// already-resolved language rather than nothing — that part, and only that
// part, would change if the facade dependency went away.
//
// The unsubscribe is discarded: the entry module's listener lives for the life
// of the page.
syncDocumentLanguage(i18n);

// loads and inits i18n + gets the translations from the frontend
i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        translation: enTranslation,
      },
      de: {
        translation: deTranslation,
      },
      es: {
        translation: esTranslation,
      },
      ca: {
        translation: caTranslation,
      },
      fr: {
        translation: frTranslation,
      },
      ja: {
        translation: jaTranslation,
      },
      nb: {
        translation: nbTranslation,
      },
      nn: {
        translation: nnTranslation,
      },
      sv: {
        translation: svTranslation,
      },
      vi: {
        translation: viTranslation,
      },
      zh: {
        translation: zhTranslation,
      },
    },
    debug: false,
    fallbackLng: {
      'en-US': ['en'],
      'en-GB': ['en'],
      de: ['de'],
      es: ['es'],
      ca: ['ca'],
      fr: ['fr'],
      ja: ['ja'],
      'no-NB': ['nb'],
      'no-NN': ['nn'],
      sv: ['sv'],
      vi: ['vi'],
      zh: ['zh'],
      'zh-CN': ['zh'],
      'zh-TW': ['zh'],
      default: ['en'],
    },
    returnNull: false,
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
  });

// Dev builds only, and before the first render so it is in place before RTK
// Query asks for the account. See the README.
installDevRoleOverride();

const root = document.getElementById('root');
if (root == null) {
  throw new Error('Cannot render React app when root element is missing!');
}

createRoot(root).render(
  <StrictMode>
    <ReduxProvider store={reduxStore}>
      <App />
      <ToastContainer position="top-right" autoClose={2000} pauseOnHover theme="light" />
    </ReduxProvider>
  </StrictMode>,
);
